(ns fen.window
  "What this window is looking at, and how it stays right.

  The view - root, what is unfolded, where the cursor is - lives here, in
  the client, and travels with every call. So the server remembers no
  window: a reload, a restart, a second tab cost nothing (see web-app)."
  (:require [fen.api :as api]
            [fen.auth :as auth]
            [kc.core :as kc]
            [kc.spa :as spa]))

(def poll-ms 2000)

(defonce WINDOW
  (atom {:view   {:root :ento :expanded #{} :date nil :cursor nil}
         :screen nil
         :rev    0
         :link   :waiting
         :status "loading ..."}))

(defn view [] (:view @WINDOW))

(defn menu
  "The popup, when one is up: the handle it was opened on - nil for the
  entity itself - and which of the offers is picked out.

  Next to the view and not in it. The view travels to the server because the
  server has to draw with it; a popup is this window asking itself what can
  be done here, and the answer is already in the screen it was given."
  []
  (:menu @WINDOW))

(defn open-menu! [handle] (swap! WINDOW assoc :menu {:handle handle :index 0}))

(defn shown
  "Text the server handed over to be read, when there is some: a title and
  the text itself. What it means is not this window's business - it is put
  in front of the person and taken away again when they are done with it
  (see ento.presenter)."
  []
  (:shown @WINDOW))

(defn close-show! [] (swap! WINDOW dissoc :shown))

(defn ask
  "An action waiting for its input, when one is: the offer it belongs to.
  What it wants - a line of text, a yes - is in the action itself; the
  window only puts the question up and hands the answer back
  (see fen.command)."
  []
  (:ask @WINDOW))

(defn ask! [offer] (swap! WINDOW assoc :ask offer))

(defn close-ask! [] (swap! WINDOW dissoc :ask))

(defn close-menu! [] (swap! WINDOW dissoc :menu))

(defn menu-index! [i] (swap! WINDOW assoc-in [:menu :index] i))

(defn open!
  "That entity becomes the root of this window. A navigation and not a
  redraw: the URL carries the root, so a window really is an address, the
  back button goes back, and a second window is a second tab.

  Nothing is drawn here. Pushing the address is the whole of it - what
  arrives at the other end is the effect in fen.main, which is also where a
  typed address and the back button arrive (see kc.spa)."
  [id]
  (spa/goto-location! (name id)))

(defn- moved-to
  "The entity the server has moved this window to, when its answer is about
  another one than the question was.

  Opening a row is the only thing that does this, and the window is told
  none of that - it compares two roots. What :open is, and which row points
  where, stays on the server side of the line (see ento.presenter)."
  [answer]
  (let [id (:root (:view answer))]
    (when (and id (not= id (:root (view)))) id)))

(defn- arrived!
  "The answer, into the window. :show only arrives when something was asked
  for that answers with text; a draw that does not carry one leaves what is
  up alone, because the poll redraws while a person is still reading.

  An answer from somewhere else is not taken at all. It would be a screen
  the address bar knows nothing about - so the address is pushed instead and
  the window arrives the way it arrives from a link, one draw later. One
  path for having moved, whichever end it started at."
  [answer]
  (auth/ok!)
  (if-let [elsewhere (moved-to answer)]
    (open! elsewhere)
    (swap! WINDOW merge
           (cond-> {:status nil :link :up :rev (or (:rev answer) 0)}
             (:view answer)   (assoc :view (:view answer))
             (:screen answer) (assoc :screen (:screen answer))
             (:show answer)   (assoc :shown (:show answer))))))

(defn- failed!
  "Two different failures that must not be told the same way: not being let
  in is something a person can do something about, a server that is not
  there is not (see fen.auth)."
  [e]
  (if (api/unauthorized? e)
    (do (auth/needed!)
        (swap! WINDOW assoc :status nil))
    (do (kc/log-error "ento server unreachable" e)
        (swap! WINDOW assoc :status "server unreachable" :link :down))))

(defn draw!
  "Ask for the whole screen again. Not a diff - the honest, slow version,
  and the right one while the surface is still being found out."
  []
  (-> (api/post-screen (view) nil)
      (.then arrived!)
      (.catch failed!)))

(defn cursor!
  "Where the cursor stands. Set here and not asked for: it changes nothing
  in the graph, it travels with the next event like the rest of the view,
  and it is drawn from the view rather than from the screen - the screen is
  the server's last answer and the cursor has moved since (see fen.keys)."
  [handle]
  (swap! WINDOW assoc-in [:view :cursor] handle))

(defn send!
  "An event out, a new view and a new screen back."
  [event]
  (-> (api/post-screen (view) event)
      (.then arrived!)
      (.catch failed!)))

(defn root!
  "Point this window at an entity, without touching the browser's history."
  [id]
  (when (and id (not= id (:root (view))))
    (swap! WINDOW #(-> %
                       (assoc :view {:root id :expanded #{} :date nil :cursor nil})
                       ;; Whatever the popup was offering, and whatever is
                       ;; up to be read, was about the entity we are leaving.
                       (dissoc :menu :shown :ask)))
    (draw!)))

(defn- tick!
  "The poll. Ask with the revision we are standing on; usually the answer is
  that number and nothing else, and only when the graph moved do we draw
  again. Hejmo's board sync, one floor up."
  []
  (-> (api/poll (:rev @WINDOW))
      (.then (fn [answer] (when (:changed? answer) (draw!))))
      (.catch (fn [_] nil))))

(defonce TIMER (atom nil))

(defn start! []
  (draw!)
  (when-not @TIMER
    (reset! TIMER (js/setInterval tick! poll-ms))))
